import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analytics } from '@/api/analytics';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import type {
  AnalyticsCallRow,
  CallNodePathRow,
  IvrSummary,
  NodeHitRow,
} from '@/api/analytics';
import { ivrTrees } from '@/api/ivrTrees';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/DateTimePicker';
import { cn } from '@/lib/cn';
import {
  BarChart3, PhoneCall, Users, Clock, X, ChevronRight,
} from 'lucide-react';

/**
 * `from` / `to` are kept as YYYY-MM-DD strings so the backend's
 * date-only filtering stays straightforward. DateTimePicker wants
 * YYYY-MM-DDTHH:MM, so we bridge at the picker boundary: append a
 * default time on read, slice it off on change.
 */
function dateOnlyToFull(d: string, defaultTime: string): string {
  if (!d) return '';
  return d.length > 10 ? d : `${d}T${defaultTime}`;
}
function fullToDateOnly(d: string): string {
  return d ? d.slice(0, 10) : '';
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function fmtMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
}

function fmtMinutes(n: number): string {
  if (n >= 60) {
    const h = Math.floor(n / 60);
    const m = n % 60;
    return `${h}h ${m}m`;
  }
  return `${n}m`;
}

function isoToDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Deterministic color ramp for node-type chips. A small accent palette
// rather than the rainbow — keeps the table scanable at a glance.
const NODE_TYPE_TONE: Record<string, string> = {
  menu: 'bg-violet-50 text-violet-700 ring-violet-200',
  playback: 'bg-blue-50 text-blue-700 ring-blue-200',
  bulletin: 'bg-blue-50 text-blue-700 ring-blue-200',
  voicemail: 'bg-teal-50 text-teal-700 ring-teal-200',
  conference: 'bg-teal-50 text-teal-700 ring-teal-200',
  survey: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  quiz: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  payment: 'bg-amber-50 text-amber-700 ring-amber-200',
  shopping: 'bg-amber-50 text-amber-700 ring-amber-200',
  transfer: 'bg-orange-50 text-orange-700 ring-orange-200',
  sip_connect: 'bg-orange-50 text-orange-700 ring-orange-200',
  extension: 'bg-orange-50 text-orange-700 ring-orange-200',
  jump: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  hangup: 'bg-rose-50 text-rose-700 ring-rose-200',
};

function NodeTypeChip({ type }: { type: string }) {
  const tone = NODE_TYPE_TONE[type] ?? 'bg-slate-50 text-slate-600 ring-slate-200';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
        tone,
      )}
    >
      {type}
    </span>
  );
}

function DtmfPill({ digits }: { digits: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 font-mono">
      {digits.split('').map((d, i) => (
        <span
          key={i}
          className={cn(
            'inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md',
            'border border-slate-700 bg-slate-800 px-1.5 text-[11px] font-semibold text-slate-100',
            'shadow-sm leading-none',
          )}
        >
          {d}
        </span>
      ))}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Summary cards
// ──────────────────────────────────────────────────────────────────────

function SummaryCards({
  summary,
  isLoading,
}: {
  summary: IvrSummary | undefined;
  isLoading: boolean;
}) {
  const cards = [
    {
      label: 'Total calls',
      value: summary ? summary.total_calls.toLocaleString() : '—',
      icon: PhoneCall,
      accent: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Unique callers',
      value: summary ? summary.unique_callers.toLocaleString() : '—',
      icon: Users,
      accent: 'text-violet-600 bg-violet-50',
    },
    {
      label: 'Total minutes',
      value: summary ? fmtMinutes(summary.total_minutes) : '—',
      icon: Clock,
      accent: 'text-emerald-600 bg-emerald-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className={cn(
            'relative overflow-hidden rounded-xl border border-border bg-card',
            'px-5 py-4 shadow-sm',
          )}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {c.label}
            </span>
            <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md', c.accent)}>
              <c.icon className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="mt-2 text-3xl font-semibold tracking-tight text-foreground tabular-nums">
            {isLoading ? (
              <span className="inline-block h-8 w-20 animate-pulse rounded bg-slate-200" />
            ) : (
              c.value
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Call path drilldown — slides over from the right
// ──────────────────────────────────────────────────────────────────────

function CallPathDrawer({
  callId,
  onClose,
}: {
  callId: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['analyticsCall', callId],
    queryFn: () => analytics.call(callId),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const call = data?.call;
  const path = data?.path ?? [];

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      aria-modal="true"
      role="dialog"
      aria-label="Call path detail"
    >
      <div className="fixed inset-0 bg-black/20" onClick={onClose} aria-hidden />
      <div className="relative z-50 flex h-full w-full max-w-[560px] flex-col bg-background shadow-2xl">
        {/* Header */}
        <div className="border-b border-border px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Call path
              </div>
              <div className="mt-1 font-mono text-sm text-foreground truncate">
                {call ? (
                  <>
                    {call.from_number ?? '—'}
                    <span className="mx-2 text-slate-300">→</span>
                    {call.to_number ?? '—'}
                  </>
                ) : (
                  'Loading…'
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {call && (
            <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Started</div>
                <div className="mt-0.5 text-foreground">{formatLocalTime(call.started_at)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Duration</div>
                <div className="mt-0.5 font-mono tabular-nums text-foreground">
                  {fmtMs(call.duration_ms)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Outcome</div>
                <div className="mt-0.5 text-foreground font-mono text-[11px]">
                  {call.end_reason ?? call.hangup_cause ?? '—'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {isLoading && (
            <div className="text-sm text-muted-foreground">Loading call path…</div>
          )}
          {error && (
            <div role="alert" className="text-sm text-red-500">
              {error instanceof Error ? error.message : 'Failed to load call path.'}
            </div>
          )}

          {call && path.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No node visits recorded for this call.
            </p>
          )}

          {path.length > 0 && (
            <ol className="relative border-l border-slate-200 pl-4 space-y-4">
              {path.map((step, i) => (
                <PathStep key={step.id} step={step} index={i} isLast={i === path.length - 1} />
              ))}
            </ol>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border px-5 py-3">
          <span className="font-mono text-[11px] text-muted-foreground truncate">
            {callId}
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function PathStep({
  step,
  index,
  isLast,
}: {
  step: CallNodePathRow;
  index: number;
  isLast: boolean;
}) {
  return (
    <li className="relative">
      {/* Dot on the rail */}
      <span
        className={cn(
          'absolute -left-[1.35rem] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-background',
          isLast ? 'bg-slate-400' : 'bg-primary',
        )}
        aria-hidden
      />
      <div className="rounded-lg border border-border bg-card px-3.5 py-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-muted-foreground tabular-nums">
                #{index.toString().padStart(2, '0')}
              </span>
              <span className="text-sm font-medium text-foreground truncate">
                {step.node_name ?? <span className="text-muted-foreground italic">Untitled node</span>}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <NodeTypeChip type={step.node_type} />
              {step.exit_reason && (
                <span className="text-[11px] text-muted-foreground font-mono">
                  → {step.exit_reason}
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono text-[11px] text-muted-foreground tabular-nums">
              {fmtMs(step.duration_ms)}
            </div>
          </div>
        </div>
        {step.dtmf_input && (
          <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Input
            </span>
            <DtmfPill digits={step.dtmf_input} />
          </div>
        )}
      </div>
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Node hits table
// ──────────────────────────────────────────────────────────────────────

function NodeHitsSection({
  rows,
  isLoading,
}: {
  rows: NodeHitRow[] | undefined;
  isLoading: boolean;
}) {
  const tableRows = (rows ?? []).map((r) => ({ ...r, id: r.node_id }));

  const maxHits = useMemo(() => {
    if (!rows || rows.length === 0) return 1;
    return Math.max(...rows.map((r) => r.hits));
  }, [rows]);

  const columns = [
    {
      header: 'Node',
      cell: (row: NodeHitRow) => (
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">
            {row.node_name ?? (
              <span className="italic text-muted-foreground">Untitled node</span>
            )}
          </span>
          <NodeTypeChip type={row.node_type} />
        </div>
      ),
    },
    {
      header: 'Hits',
      headClassName: 'text-right',
      className: 'text-right',
      cell: (row: NodeHitRow) => (
        <div className="flex items-center justify-end gap-3">
          <div className="w-24">
            <div
              className="h-1.5 rounded-full bg-primary/80"
              style={{
                width: `${Math.max(4, Math.round((row.hits / maxHits) * 100))}%`,
              }}
            />
          </div>
          <span className="font-mono tabular-nums text-sm font-medium text-foreground">
            {row.hits.toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      header: 'Unique callers',
      headClassName: 'text-right',
      className: 'text-right',
      cell: (row: NodeHitRow) => (
        <span className="font-mono tabular-nums text-sm text-foreground">
          {row.unique_callers.toLocaleString()}
        </span>
      ),
    },
    {
      header: 'Avg. duration',
      headClassName: 'text-right',
      className: 'text-right',
      cell: (row: NodeHitRow) => (
        <span className="font-mono tabular-nums text-sm text-muted-foreground">
          {fmtMs(row.avg_duration_ms)}
        </span>
      ),
    },
  ];

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground">Per-node hits</h2>
        {rows && rows.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {rows.length} node{rows.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="rounded-lg border border-border bg-card p-10 text-sm text-muted-foreground text-center">
          Loading node hits…
        </div>
      ) : (
        <DataTable
          rows={tableRows}
          columns={columns}
          empty={
            <span>
              No node visits recorded in this window.
              <br />
              <span className="text-xs">Analytics start populating once calls arrive.</span>
            </span>
          }
        />
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Recent calls table
// ──────────────────────────────────────────────────────────────────────

function RecentCallsSection({
  rows,
  isLoading,
  onSelect,
}: {
  rows: AnalyticsCallRow[] | undefined;
  isLoading: boolean;
  onSelect: (id: string) => void;
}) {
  const columns = [
    {
      header: 'Started',
      cell: (r: AnalyticsCallRow) => (
        <span
          className="text-sm text-muted-foreground"
          title={formatLocalTime(r.started_at)}
        >
          {formatRelative(r.started_at)}
        </span>
      ),
    },
    {
      header: 'From',
      cell: (r: AnalyticsCallRow) => (
        <span className="font-mono text-sm text-foreground">{r.from_number ?? '—'}</span>
      ),
    },
    {
      header: 'Duration',
      headClassName: 'text-right',
      className: 'text-right',
      cell: (r: AnalyticsCallRow) => (
        <span className="font-mono tabular-nums text-sm text-foreground">
          {fmtMs(r.duration_ms)}
        </span>
      ),
    },
    {
      header: 'Outcome',
      cell: (r: AnalyticsCallRow) => (
        <span className="font-mono text-xs text-muted-foreground">
          {r.end_reason ?? r.hangup_cause ?? '—'}
        </span>
      ),
    },
    {
      header: '',
      headClassName: 'w-8',
      className: 'w-8 text-right',
      cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground inline" />,
    },
  ];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground">Recent calls</h2>
      </div>
      {isLoading ? (
        <div className="rounded-lg border border-border bg-card p-10 text-sm text-muted-foreground text-center">
          Loading calls…
        </div>
      ) : (
        <DataTable
          rows={rows ?? []}
          columns={columns}
          onRowClick={(r) => onSelect(r.id)}
          empty="No calls in this window."
        />
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from: isoToDateInput(from), to: isoToDateInput(to) };
}

export function IvrAnalyticsPage() {
  const effectiveCustomerId = useEffectiveCustomerId();
  const init = defaultRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [treeId, setTreeId] = useState<string>('');
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const { data: treeList } = useQuery({
    queryKey: ['ivrTrees', 'analytics-picker', effectiveCustomerId],
    queryFn: () => ivrTrees.list({ limit: 200, customer_id: effectiveCustomerId ?? undefined }),
    staleTime: 5 * 60 * 1000,
  });

  const filter = {
    tree_id: treeId || undefined,
    from: from || undefined,
    to: to || undefined,
    customer_id: effectiveCustomerId ?? undefined,
  };

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['analyticsSummary', filter],
    queryFn: () => analytics.ivrSummary(filter),
  });

  const { data: nodeHits, isLoading: nodeHitsLoading } = useQuery({
    queryKey: ['analyticsNodeHits', filter],
    queryFn: () => analytics.nodeHits(filter),
  });

  const { data: calls, isLoading: callsLoading } = useQuery({
    queryKey: ['analyticsCalls', filter, 50],
    queryFn: () => analytics.calls({ ...filter, limit: 50 }),
  });

  const activeFilters = !!(from || to || treeId);

  return (
    <>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <BarChart3 className="h-3 w-3" />
            Reports
          </span>
        }
        title="IVR analytics"
        description="Per-tree, per-node call volumes, unique callers, and average listen time. Click any call to see the node-by-node path with DTMF presses."
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">From</span>
          <DateTimePicker
            value={dateOnlyToFull(from, '00:00')}
            onChange={(v) => setFrom(fullToDateOnly(v))}
            defaultTimeOnDateSelect="00:00"
            placeholder="Start"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">To</span>
          <DateTimePicker
            value={dateOnlyToFull(to, '23:59')}
            onChange={(v) => setTo(fullToDateOnly(v))}
            defaultTimeOnDateSelect="23:59"
            placeholder="End"
          />
        </div>
        <Select
          value={treeId || '__all__'}
          onValueChange={(v) => setTreeId(v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="h-9 w-56 text-sm">
            <SelectValue placeholder="All trees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All trees</SelectItem>
            {(treeList?.rows ?? []).map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => {
              const d = defaultRange();
              setFrom(d.from);
              setTo(d.to);
              setTreeId('');
            }}
          >
            Reset to last 7 days
          </Button>
        )}
      </div>

      <SummaryCards summary={summary} isLoading={summaryLoading} />

      <NodeHitsSection rows={nodeHits?.rows} isLoading={nodeHitsLoading} />

      <RecentCallsSection
        rows={calls?.rows}
        isLoading={callsLoading}
        onSelect={(id) => setSelectedCallId(id)}
      />

      {selectedCallId && (
        <CallPathDrawer
          callId={selectedCallId}
          onClose={() => setSelectedCallId(null)}
        />
      )}
    </>
  );
}
